// src/components/Sidebar.tsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Tag,
  Boxes,
  TrendingUp,
  FileSpreadsheet,
  Database,
  Building2,
  Users,
  ShieldCheck,
  FileText,
  Settings,
  UserCircle,
  LogOut,
  X,
  CreditCard,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

interface SidebarProps {
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, setMobileOpen }) => {
  const { user, can, logout } = useAuth();
  const { settings } = useSettings();

  const navItems = [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, show: true },
    { label: 'Products & Pricing', path: '/products', icon: Tag, show: can('products.view') },
    { label: 'Stock Details & Valuation', path: '/stock-inventory', icon: Boxes, show: can('products.view') },
    { label: 'Production & Stock Ledger', path: '/production-batches', icon: Database, show: can('products.view') },
    { label: 'Sales & GP Tracker', path: '/sales-profitability', icon: TrendingUp, show: true },
    { label: 'Invoices Ledger', path: '/invoices', icon: FileSpreadsheet, show: true },
    { label: 'Credit Applications', path: '/credit-applications', icon: CreditCard, show: can('credit_applications.view') || true },
    { label: 'Audit & Change Logs', path: '/audit-logs', icon: FileText, show: can('audit.view') },
  ];

  const adminItems = [
    { label: 'Departments', path: '/departments', icon: Building2, show: can('departments.view') },
    { label: 'Users', path: '/users', icon: Users, show: can('users.view') },
    { label: 'Roles & Permissions', path: '/roles', icon: ShieldCheck, show: can('roles.view') },
  ];

  const systemItems = [
    { label: 'System Settings', path: '/settings', icon: Settings, show: can('settings.view') },
  ];

  const hasAdminItems = adminItems.some(item => item.show);
  const hasSystemItems = systemItems.some(item => item.show);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
      isActive
        ? 'bg-[#0f5a2e] text-white shadow-sm ring-1 ring-emerald-500/20 font-semibold'
        : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
    }`;

  const sidebarContent = (
    <div className="flex flex-col h-full bg-slate-900 text-slate-100 border-r border-slate-800 select-none">
      {/* Brand Header with Official Geobit Logo */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/80 bg-slate-950/40">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <img
              src="/geobit-logo-white.svg"
              alt="Geobit Group"
              className="h-8 w-auto max-w-[175px] object-contain"
            />
          </div>
          <span className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mt-1.5 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            Department & Pricing Portal
          </span>
        </div>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden text-slate-400 hover:text-white p-1 rounded-md"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
        {/* Core Modules */}
        <div className="space-y-1">
          {navItems.filter(item => item.show).map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={linkClass}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>

        {/* Administration Section */}
        {hasAdminItems && (
          <div>
            <h2 className="px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
              Administration
            </h2>
            <div className="space-y-1">
              {adminItems.filter(item => item.show).map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={linkClass}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {/* System Section */}
        {hasSystemItems && (
          <div>
            <h2 className="px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
              System
            </h2>
            <div className="space-y-1">
              {systemItems.filter(item => item.show).map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={linkClass}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* User Footer Card */}
      <div className="p-4 border-t border-slate-800 space-y-1">
        <NavLink
          to="/profile"
          onClick={() => setMobileOpen(false)}
          className={linkClass}
        >
          <UserCircle className="w-4 h-4 flex-shrink-0" />
          <div className="truncate text-xs">
            <p className="font-medium text-white truncate">{user?.fullName}</p>
            <p className="text-slate-400 truncate">{user?.role?.name}</p>
          </div>
        </NavLink>

        <button
          type="button"
          onClick={() => {
            setMobileOpen(false);
            logout();
          }}
          className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-rose-300 hover:bg-rose-950/40 hover:text-rose-200 transition-colors"
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Static Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 z-30">
        {sidebarContent}
      </aside>

      {/* Mobile Drawer Backdrop and Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-slate-900 z-10">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
};
